import './selectorLayout.scss';

import { ReactNode } from 'react';
import { Collapse } from '@eliberty/design-system-react';
import { Popover } from '@eliberty/design-system-react/mantine';

/**
 * Display mode for selector components.
 * - `collapse`: content revealed inline via Collapse animation (mobile/default).
 * - `popover`: content displayed in a floating Popover dropdown (desktop bar).
 */
export type DisplayMode = 'collapse' | 'popover';

/** Typed constants to avoid magic strings when referencing display modes. */
export const DISPLAY_MODE: Record<Uppercase<DisplayMode>, DisplayMode> = {
  COLLAPSE: 'collapse',
  POPOVER: 'popover',
} as const;

interface SelectorLayoutProps {
  displayMode?: DisplayMode;
  isOpen: boolean;
  onToggle: () => void;
  onClickOutside?: () => void;
  header: ReactNode;
  children: ReactNode;
  popoverWidth?: number | string;
}

/**
 * Structural layout atom that abstracts the open/close mechanism of a selector.
 *
 * In **collapse** mode, `children` are rendered inline below `header` via
 * a `<Collapse>` animation.
 *
 * In **popover** mode, `header` becomes the `Popover.Target` (full-area
 * click zone) and `children` appear in a floating `Popover.Dropdown`.
 *
 * This component does **not** manage state — the parent controls `isOpen` and
 * `onToggle`. It only decides *how* the content is displayed.
 *
 * @see SelectorHeader — presentational companion that renders labels,
 *   selected values and the Edit/Cancel toggle button.
 */
const SelectorLayout = ({
  displayMode = DISPLAY_MODE.COLLAPSE,
  isOpen,
  onToggle,
  onClickOutside,
  header,
  children,
  popoverWidth = 450,
}: SelectorLayoutProps) => {
  if (displayMode === DISPLAY_MODE.POPOVER) {
    return (
      <Popover
        opened={isOpen}
        onChange={(nextOpened) => {
          const isClosingExternally = isOpen && !nextOpened;
          if (isClosingExternally) onClickOutside?.();
        }}
        position='bottom-start'
        offset={8}
        width={popoverWidth}
        withinPortal={false}
        trapFocus={false}
        classNames={{ dropdown: 'SelectorLayout_dropdown' }}
      >
        <Popover.Target>
          <button
            type='button'
            className='SelectorLayout_popoverTarget'
            aria-expanded={isOpen}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            {header}
          </button>
        </Popover.Target>
        <Popover.Dropdown>
          <div className='SelectorLayout_popoverContent'>{children}</div>
        </Popover.Dropdown>
      </Popover>
    );
  }

  return (
    <>
      {header}
      <Collapse isOpen={isOpen}>{children}</Collapse>
    </>
  );
};

export default SelectorLayout;
